import { Box, Button, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from "@material-ui/core";
import { equals, isNil, path, startsWith } from "ramda";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Title, useNotify } from "react-admin";
import { useSelector } from "react-redux";

import CustomCard from "../../../../components/CustomCard";
import FormWrapper from "../../../../components/FormWrapper";
import Loader from "../../../../components/Loader";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import { ReduxState } from "../../../../types/redux.types";
import { ILabeledValue } from "../../../../types/resource.types";
import { OrdersDeliveryMethods } from "../../../../types/resources/enums";
import { brandsDeliveryMethodsSubCollRef } from "../../../../utils/referencies";
import { applyCreateTimestamp, applyUpdateTimestamp } from "../../../../utils/timestamp";
import { OrderDeliveryMethods } from "../../../Orders/types";
import { OrderMethodsState } from "../../types";

const HOURS_SECONDS_CONVERSION_NUMBER = 3600;

const DeliveryMethods = () => {
  const ordersDeliveryMethods = useSelector<ReduxState, ILabeledValue>((state) => state.enums.ordersDeliveryMethods);

  const [enumsDeliveryMethodsEnums, setDeliveryMethodsEnums] = useState<string[]>([]);
  const [error, setError] = useState<any>({});

  /**
   * `originalBrandOrderMethods` is used as a cached value to compare it with updated `finalBrandOrderMethods`
   * to update only changed documents
   * */
  const [originalBrandOrderMethods, setOriginalBrandOrderMethods] = useState<OrderMethodsState>({});
  const [brandOrderMethods, setBrandOrderMethods] = useState<OrderMethodsState>({});
  const [finalBrandOrderMethods, setFinalBrandOrderMethods] = useState<OrderMethodsState>({});

  /**
   * random number generated to update the component after submit the form
   * to ensure that we fetch latest documents to inject `ref` to all documents to avoid duplication
   * */
  const [random, setRandom] = useState<any>(undefined);
  const [fetchMethodsLoading, setFetchMethodsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const notify = useNotify();
  const delayRef = useRef<any>(null);
  const { user } = useCurrentUser();

  const handleSelectedMethod = (e: ChangeEvent<HTMLInputElement>, id: string): void => {
    const active = e.target.checked;
    const modifiedBrandMethods = {
      ...brandOrderMethods,
      [id]: {
        ...brandOrderMethods[id],
        data: {
          ...brandOrderMethods[id]?.data,
          active,
        },
      },
    };
    setBrandOrderMethods(modifiedBrandMethods);
  };

  const isDisabledBtn = (): boolean => {
    return (
      Object.values(error).some((value: any) => !!value.duration) ||
      Object.values(finalBrandOrderMethods).every((value: any) => !value.data.active) ||
      submitLoading
    );
  };

  const handleSavaMethods = async (): Promise<void> => {
    for await (const [deliveryMethodId, entry] of Object.entries(finalBrandOrderMethods)) {
      if (entry.ref) {
        if (!equals(originalBrandOrderMethods[deliveryMethodId].data, entry.data)) {
          setSubmitLoading(true);
          /**
           * make sure not to update nominated duration if it has a date value
           * */
          if (
            deliveryMethodId === OrdersDeliveryMethods.nominated &&
            !originalBrandOrderMethods[deliveryMethodId].data.duration
          )
            entry.data.duration = null;
          /**
           * convert selected user `nextDay` delivery method to seconds
           * */
          if (deliveryMethodId === OrdersDeliveryMethods.nextDay)
            entry.data.duration = (entry.data.duration as number) * HOURS_SECONDS_CONVERSION_NUMBER;
          const data = applyUpdateTimestamp(entry.data);
          console.log("Updating delivery method: ", data);
          try {
            await entry.ref.update(data);
            setRandom(Math.random());
            setSubmitLoading(false);
            notify("Methods has been updated", "success");
          } catch (ex: any) {
            console.log(ex.message);
            setSubmitLoading(false);
            notify("Error on update methods", "error");
          }
        }
      } else {
        setSubmitLoading(true);
        try {
          if (deliveryMethodId === OrdersDeliveryMethods.nominated) entry.data.duration = null;
          /**
           * convert selected user `nextDay` delivery method to seconds
           * */
          if (deliveryMethodId === OrdersDeliveryMethods.nextDay)
            entry.data.duration = (entry.data.duration as number) * HOURS_SECONDS_CONVERSION_NUMBER;
          const data = applyCreateTimestamp({ ...entry.data, name: deliveryMethodId });
          await brandsDeliveryMethodsSubCollRef(user.id as string)
            .doc(deliveryMethodId)
            .set(data);
          setRandom(Math.random());
          setSubmitLoading(false);
          notify("Methods has been updated", "success");
        } catch (ex: any) {
          console.log(ex.message);
          setSubmitLoading(false);
          notify("Error on update methods", "error");
        }
      }
    }
  };

  const handleChangeInput = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: string,
    field: string
  ): void => {
    setBrandOrderMethods((prevState: OrderMethodsState) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        data: {
          ...prevState[id].data,
          [field]: startsWith("0", e.target.value) ? e.target.value.replace(/^0+/, "") : +e.target.value,
        },
      },
    }));
  };

  /**
   * update `setFinalBrandOrderMethods` after a given milliseconds to let user type for a while
   * and then fire any dependant actions
   * */
  useEffect(() => {
    delayRef.current = setTimeout(() => {
      setFinalBrandOrderMethods(brandOrderMethods);
    }, 350);

    return () => {
      clearTimeout(delayRef.current);
    };
  }, [brandOrderMethods]);

  /**
   * handle available methods from enums
   * */
  useEffect(() => {
    if (!isNil(ordersDeliveryMethods)) {
      setDeliveryMethodsEnums(Object.keys(ordersDeliveryMethods).sort());
    }
  }, [ordersDeliveryMethods]);

  /**
   * handle errors
   * */
  useEffect(() => {
    const errorResult = Object.entries(finalBrandOrderMethods).reduce((acc, [id, value]) => {
      if (id === OrdersDeliveryMethods.nextDay && value.data.active && value.data.duration! > 0) {
        return {
          ...acc,
          [id]: {
            duration: value.data.active && value.data.duration! > 24 ? "Duration should be less than 24" : undefined,
          },
        };
      }
      return {
        ...acc,
        [id]: {
          duration:
            (value.data.active && value.data.duration === 0 && id !== OrdersDeliveryMethods.nominated) ||
            (value.data.active && !value.data.duration && id !== OrdersDeliveryMethods.nominated)
              ? "Duration should be more than 0"
              : undefined,
        },
      };
    }, {});
    setError(errorResult);
  }, [finalBrandOrderMethods]);

  /**
   * fetch documents
   * */
  useEffect(() => {
    /**
     * todo - we might change this to listener as client can change nominated duration by himself
     * and makes conflicts if brand is opening the page for long time and then decided to update methods
     * */
    if (user.id) {
      setFetchMethodsLoading(true);
      brandsDeliveryMethodsSubCollRef(user.id)
        .get()
        .then((snapshot) => {
          if (!snapshot.empty) {
            const preparedMethods = snapshot.docs.reduce((acc, docSnapshot) => {
              const method: any = docSnapshot.data();
              return {
                ...acc,
                [method.name]: {
                  /**
                   * we inject `ref` to fetched documents to use it in update record directly
                   * */
                  ref: docSnapshot.ref,
                  data: {
                    active: method.active,
                    fees: method.fees,
                    duration:
                      method.name === OrderDeliveryMethods.nextDay
                        ? typeof method.duration === "number"
                          ? method.duration / HOURS_SECONDS_CONVERSION_NUMBER
                          : method.duration
                        : method.duration,
                  },
                },
              };
            }, {});
            setBrandOrderMethods(preparedMethods);
            setOriginalBrandOrderMethods(preparedMethods);
          }
          setFetchMethodsLoading(false);
        })
        .catch((ex: any) => {
          console.log(ex.message);
          setFetchMethodsLoading(false);
        });
    }
  }, [user.id, random]);

  return (
    <FormWrapper>
      <Title title="Delivery Methods" />
      <CustomCard title="Delivery Methods">
        <Loader loading={fetchMethodsLoading}>
          {enumsDeliveryMethodsEnums.length > 0 ? (
            <>
              {enumsDeliveryMethodsEnums.map((id) => (
                <Box my={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={path<boolean>([id, "data", "active"])(brandOrderMethods)}
                        onChange={(e) => handleSelectedMethod(e, id)}
                        name={id}
                      />
                    }
                    label={ordersDeliveryMethods[id].label.en}
                  />
                  <Box my={1} mx={-1}>
                    {/**
                     * `nominated` method type should be null, so we shouldn't make user try to change this
                     * it will be updated to `null` before submit
                     */}
                    {id !== OrdersDeliveryMethods.nominated && (
                      <TextField
                        label={`${
                          id === OrdersDeliveryMethods.standard
                            ? "Duration (In Days)"
                            : id === OrdersDeliveryMethods.nextDay
                            ? "Day Beginning (In Hours)"
                            : ""
                        }`}
                        variant="outlined"
                        size="small"
                        disabled={!path<boolean>([id, "data", "active"])(finalBrandOrderMethods)}
                        error={!!path<string | undefined>([id, "duration"])(error)}
                        helperText={path<string | undefined>([id, "duration"])(error)}
                        value={
                          path([id, "data", "duration"])(brandOrderMethods) === 0
                            ? null
                            : path([id, "data", "duration"])(brandOrderMethods)
                        }
                        type="number"
                        onChange={(e) => handleChangeInput(e, id, "duration")}
                        style={{ margin: 8 }}
                      />
                    )}
                    <TextField
                      label="fees"
                      variant="outlined"
                      size="small"
                      disabled={!path<boolean>([id, "data", "active"])(finalBrandOrderMethods)}
                      value={
                        path([id, "data", "fees"])(brandOrderMethods)
                          ? path([id, "data", "fees"])(brandOrderMethods)
                          : 0
                      }
                      type="number"
                      onChange={(e) => handleChangeInput(e, id, "fees")}
                      style={{ margin: 8 }}
                    />
                  </Box>
                </Box>
              ))}

              <Button
                variant="contained"
                color="primary"
                startIcon={submitLoading ? <CircularProgress color="primary" size={12} /> : null}
                disabled={isDisabledBtn()}
                onClick={handleSavaMethods}
              >
                Save
              </Button>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography variant="body2" color="textSecondary">
                Getting Options...
              </Typography>
            </Box>
          )}
        </Loader>
      </CustomCard>
    </FormWrapper>
  );
};

export default DeliveryMethods;
